import { useCallback, useContext, useEffect, useState } from "react"
import dayjs from "dayjs"
import { faAngleDoubleLeft, faEdit, faPrint } from "@fortawesome/free-solid-svg-icons"

import { Table } from 'antd';

import { ApplicationContext } from 'context'

import ButtonPrimary from "components/ButtonPrimary"
import Header from "components/Header"
import PowerForm from "components/PowerForm"
import StatsTile from "components/StatTile"
import ViewPDF from "components/ViewPDF"

import Filter from "./Filter"

import api from "lib/api"
import { formatMoney, discectError } from "lib/utils"

import styles from "./styles.module.scss"

const InventoryDashboard = ({ surety }) => {
  const { contextAgency, contextError, contextLoading } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [_, setError] = contextError // eslint-disable-line
  const [agency] = contextAgency // eslint-disable-line

  const [employees, setEmployees] = useState(null)
  const [preFormattedPowers, setPreFormattedPowers] = useState(null)
  const [powers, setPowers] = useState(null)
  const [editingPower, setEditingPower] = useState(null)
  const [viewPdf, setViewPdf] = useState(null)
  const [filter, setFilter] = useState({ dateType: 'PAYMENT_DATE', dates: [dayjs().subtract(7, 'days'), dayjs()] })

  const agencyId = agency.id

  const { agency: filterAgency } = filter

  const _fetchPowers = useCallback(async () => {
    setIsLoading(true)
    try {
      const { powers: incomingPowers } = await api.powers.fetchPowers(agencyId, {})

      const newPowers = incomingPowers
        .map(itm => {
          return {
            id: itm.fb_id,
            powerNumber: itm.po_number,
            defendantName: itm.meta_data.defendantName || '',
            prefix: itm.prefix || '',
            number: itm.number || '',
            bondStatus: itm.status || '',
            executionDate: itm.executed_at ? dayjs(itm.executed_at).format('MM/DD/YYYY') : null,
            executionDateTs: null,
            isExonerated: itm.is_exonerated || false,
            isReported: itm.is_reported || false,
            powerAmount: itm.bond_value ? formatMoney(itm.bond_value) : 0,
            bondAmount: itm.meta_data.bondAmount ? formatMoney(itm.meta_data.bondAmount) : 0,
            premiumAmount: itm.meta_data.premiumAmount ? formatMoney(itm.meta_data.premiumAmount) : 0,
            actualBuff: itm.meta_data.actualBuff ? formatMoney(itm.meta_data.actualBuff) : 0,
            actualRate: itm.meta_data.actualRate ? formatMoney(itm.meta_data.actualRate) : 0,
            buff: itm.meta_data.buff ? formatMoney(itm.meta_data.buff) : null,
            rate: itm.meta_data.rate ? formatMoney(itm.meta_data.rate) : null,
            packageName: itm.packageName,
            package: {
              id: itm.packageId,
              name: itm.packageName,
              status: itm.packageStatus
            },
            agency: {
              id: itm.agencyId,
              name: itm.agencyName
            }
          }
        })

      setPowers(() => newPowers)
      setPreFormattedPowers(() => incomingPowers)
    } catch(e) {
      console.error(e)
      setError(discectError(e))
    }
    setIsLoading(false)
  }, [agencyId, setError, setIsLoading])

  useEffect(() => {
    if (!powers) _fetchPowers() 
  }, [_fetchPowers, agencyId, powers, filterAgency, setError, setIsLoading])
  
  useEffect(() => {
    const _fetch = async () => {
      setIsLoading(true)
      try {
        const { employees: incomingEmployees } = await api.employees.all()
        setEmployees(() => incomingEmployees)
        setIsLoading(false)
        return employees
      } catch(e) {
        setError(discectError(e))
      }
      setIsLoading(false)
    }
    if (!employees) _fetch()
  }, [employees, setError, setIsLoading])

  // Actions

  const _onSavePower = async values => {
    setError(null)

    values.executionDate = (values.executionDate && values.executionDate !== '') ? dayjs(values.executionDate.replace('_', ' ')).format('YYYY-MM-DD') : null
    values.dob = (values.dob && values.dob !== '') ? dayjs(values.dob.replace('_', ' ')).format('YYYY-MM-DD') : null

    setIsLoading(true)
    try {
      await api.powers.updatePower(agencyId, editingPower, values)
      await _fetchPowers()
      setEditingPower(null)
    } catch(e) {
      console.error(e)
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _onGeneratePower = async powerId => {
    setError(null)
    setIsLoading(true)
    try {
      const power = preFormattedPowers.find(itm => itm.fb_id === powerId)
      if (power) {
        const executedDate = dayjs(power.executed_at)
        const mapping = {
          powerValue: power.bond_value || 0,
          expirationDate: dayjs(power.expirationDate.split('T').shift()).format('DD-MMM-YY'),
          powerNumber: power.po_number,
          day: executedDate.format('D'),
          month: executedDate.format('MMMM'),
          year: executedDate.format('YYYY'),
          defendantName: power.meta_data.defendantName,
          premium: power.meta_data.premiumAmount ? formatMoney(power.meta_data.premiumAmount, true).replace('$', '') : 0,
          court: power.meta_data?.courtName || '',
          city: power.meta_data?.city || '',
          state: power.meta_data?.state || '',
          caseNumber: power.meta_data?.caseNumber || '',
          bondAmount: power.meta_data.bondAmount ? formatMoney(power.meta_data.bondAmount, true).replace('$', '') : 0,
          charge: power.meta_data?.charge || '',
          rewriteNumber: power.meta_data?.originalPowerNumber || '',
        }
        const { url, key } = await api.powers.generatePower({ suretyId: surety.id, mapping })
        if (url) {
          setViewPdf({ powerId, url, key })
        }
      }
    } catch(e) {
      console.error(e)
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _onPrintComplete = (data) => {
    console.log('On Printing Complete: ', data)
  }

  // Layout

  const _getFilteredPowers = () => {
    if (powers) {
      return powers
        .filter(power => {
          let ok = true
          if (ok && filter.search) {
            ok = (power.powerNumber.indexOf(filter.search) >= 0)
          }
          return ok
        })
      }
    return []
  }

  const _getTable = () => {
    if (powers) {   
      const columns = [
        {
          title: 'Power Number',
          dataIndex: 'powerNumber',
          key: 'powerNumber',
          align: 'left',
          render: (_, { powerNumber }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{powerNumber}</p>
              </div>
            )
          }
        },
        {
          title: 'Power Value',
          dataIndex: 'powerAmount',
          key: 'powerAmount',
          width: 120,
          align: 'center',
          render: (_, { powerAmount }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{powerAmount}</p>
              </div>
            )
          }
        },
        {
          title: 'Status',
          key: 'status',
          dataIndex: 'status',
          width: 120,
          align: 'center',
          filters: [
            {
              text: 'Open',
              value: 'OPEN'
            },
            {
              text: 'Executed',
              value: 'EXECUTED'
            },
            {
              text: 'Void',
              value: 'VOID'
            }
          ],
          onFilter: (value, record) => (record.bondStatus === value),
          render: (_, { bondStatus }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{bondStatus}</p>
              </div>
            )
          }
        },
        {
          title: 'Execution Date',
          dataIndex: 'executionDate',
          key: 'executionDate',
          width: 140,
          align: 'center',
          render: (_, { executionDate }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{executionDate || ''}</p>
              </div>
            )
          }
        },
        {
          title: 'Defendant Name',
          dataIndex: 'defendantName',
          key: 'defendantName',
          width: 200,
          align: 'center',
          render: (_, { defendantName }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{defendantName}</p>
              </div>
            )
          }
        },
        {
          title: 'Bond Amount',
          dataIndex: 'bondAmount',
          key: 'bondAmount',
          width: 120,
          align: 'center',
          render: (_, { bondAmount }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{bondAmount}</p>
              </div>
            )
          }
        },
        {
          title: 'Premium',
          dataIndex: 'premiumAmount',
          key: 'premiumAmount',
          width: 120,
          align: 'center',
          render: (_, { premiumAmount }) => {
            return (
              <div className={styles.agencyInfo}>
                <p>{premiumAmount}</p>
              </div>
            )
          }
        },
        {
          key: 'action',
          width: 120,
          render: (_, record) => {
            return (
              <div className={styles.actions}>
                <ButtonPrimary small color="orange" onClick={() => _onGeneratePower(record.id)} icon={faPrint} disabled={record.bondStatus === 'EXECUTED' ? false : true}>Print</ButtonPrimary>
                <ButtonPrimary small color="navy" onClick={() => setEditingPower(record.id)} icon={faEdit}>Edit</ButtonPrimary>
              </div>
            )
          }
        }
      ]

      return (
        <Table
          bordered
          dataSource={_getFilteredPowers()}
          columns={columns}
          size="small"
        />
      )
    }
    return null
  }

  const _getEditingPowerForm = () => {
    const power = preFormattedPowers.find(itm => itm.fb_id === editingPower)
    if (power) {
      const params = {
        bondStatus: power.status,
        prefix: power.prefix,
        powerNumber: power.number,
        caseNumber: power.meta_data?.caseNumber || '',
        powerAmount: power.bond_value,
        rate: power.meta_data?.rate || '',
        buff: power.meta_data?.buff || '',
        bondAmount: power.meta_data?.bondAmount || '',
        premium: power.meta_data?.premiumAmount || '',
        executionDate: power.executed_at ? dayjs(power.executed_at).format('MM/DD/YYYY') : '',
        defendantFirst: power.meta_data?.defendantNameParts?.nameFirst || '',
        defendantMiddle: power.meta_data?.defendantNameParts?.nameMiddle || '',
        defendantLast: power.meta_data?.defendantNameParts?.nameLast || '',
        ssn: power.meta_data?.ssn || '',
        dob: power.meta_data?.dob ? dayjs(power.meta_data.dob).format('MM/DD/YYYY') : '',
        writingFirst: power.meta_data?.writingAgent?.nameFirst || '',
        writingLast: power.meta_data?.writingAgent?.nameLast || '',
        courtName: power.meta_data?.courtName || '',
        charge: power.meta_data?.charge || '',
        city: power.meta_data?.city || '',
        state: power.meta_data?.state || '',
        county: power.meta_data?.county || '',
        actualRate: power.meta_data?.actualBuff,
        actualBuff: power.meta_data?.actualRate,
        rateOverride: (power.meta_data?.actualRate !== power.meta_data?.rate),
        buffOverride: (power.meta_data?.actualBuff !== power.meta_data?.buff)
      }
      if (agency?.contract) {
        return (
          <div className={styles.editPower}>
            <div className={styles.editPower__header}>
              <div className={styles.editPower__header__left}>
                <Header title={`Edit Power: ${power.po_number}`} instructions="Update the power below to make reporting easier." />
              </div>
              <div className={styles.editPower__header__right}>
                <StatsTile small label="VALUE" value={`${formatMoney(power.bond_value)}`} color="grey" icon={<i className="fa-solid fa-dollar"></i>} />
                <StatsTile small label="RATE" value={`${agency.contract.rate}%`} color="blue" icon={<i className="fa-solid fa-percent"></i>} />
                <StatsTile small label="BUF" value={`${agency.contract.buff}%`} color="teal" icon={<i className="fa-solid fa-percent"></i>} />
              </div>
            </div>
            <PowerForm 
              contract={agency.contract}
              employees={employees}
              newPower={params}
              onSave={_onSavePower}
              onCancel={() => {
                setEditingPower(null)
              }} 
            />
          </div>
        )
      }
      return (
        <div>
          <p>You can't modify powers until you have set the contract terms.</p>
          <ButtonPrimary small color="navy" onClick={() => setEditingPower(null)} icon={faAngleDoubleLeft}>Back</ButtonPrimary>
        </div>
      )
    }
    return (
      <div>
        <p>The power was not found.</p>
        <ButtonPrimary small color="navy" onClick={() => setEditingPower(null)} icon={faAngleDoubleLeft}>Back</ButtonPrimary>
      </div>
    )
  }

  return (
    <div className={styles.liability}>
      <Filter filters={filter} setFilters={setFilter} />
      <section className={styles.liability__rows}>
        {!editingPower && (
          <div>
            {_getTable()}
          </div>
        )}
        {editingPower && _getEditingPowerForm()}
      </section>
      {viewPdf && <ViewPDF isDisplayingPrint url={viewPdf.url} data={{ powerId: viewPdf.powerId }} text="Print the Power by clicking the Print Button" onPrintedComplete={_onPrintComplete} onClose={() => setViewPdf(null)} />}
    </div>
  )
}

export default InventoryDashboard
